import './googlemap.css'

import {
    APIProvider,
    Map,
} from '@vis.gl/react-google-maps';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { deepCopyObject, isNullOrEmpty, isNumeric } from '../../helpers/ObjectHelpers';
import { getNumericUrlParam } from '../../helpers/UrlHelpers';
import { MapContent } from './components/MapContent';
import { IMapStoreState, createMapStore } from './stores/MapStore';
import { MapStoreContext } from './context/MapStoreContext';
import { StoreApi } from 'zustand';
import { useConfiguration, useDarkmode, useMapEvent } from 'components/hooks/useAppStore';
import { useStoreSelector } from 'components/hooks/useStoreSelector';
import { useMapId, useMapConfig } from './hooks/useMapStore';
import { MapEventProps } from "components/props";
import { EventSystemNames } from "components/constants";
import { getBooleanSearchParam } from "../../helpers/UrlHelpers";
import { useSearchParams } from "react-router";

const DEBOUNCE_TIMER = 250;

interface GoogleMapProps {
    isInline?: boolean,
    height?: number | null,
    zoom?: number,
    minZoom?: number,
    showSettings?: boolean,
    showSearch?: boolean,
    showFeatures?: boolean,
    showSeamap?: boolean,
    showAis?: boolean,
    showPcs?: boolean,
    showLocations?: boolean,
    showCertificateLocations?: boolean,
    autoUpdateSearchParams?: boolean,
    showPilotageShipPosition?: boolean,
    pilotageId?: number,
    incidentPilotId?: number,
    shipId?: number,
    focusOnSegmentId?: number,
    pcsGroupId?: number | null,
    pecExam?: any,
    positionType?: string | null,
    position?: any | null
    featureCollection?: any | null,
    evaluation?: any | null,
    pecId?: number | null,
    pecApplicationId?: number | null,
    pecLatitude?: number | null,
    pecLongitude?: number | null,
    pilotCertificateCoastalSegmentPerson?: any | null,

    onClick?: (e: any) => void,
    onChange?: (e: any) => void,
    onInitFeatures?: (e: number[]) => void,
    onFeatureMouseOver?: (e: number) => void,
    onFeatureMouseOut?: () => void,
    onFeatureClick?: (e: any) => void
}

export const GoogleMap = ({
    isInline,
    height,
    zoom = 11,
    minZoom = 4,
    showSettings = true,
    showSearch = false,
    showFeatures = false,
    showSeamap = false,
    showAis = false,
    showLocations = false,
    showCertificateLocations = false,
    showPcs = false,
    autoUpdateSearchParams = false,
    showPilotageShipPosition = true,
    pilotageId = 0, 
    incidentPilotId = 0,
    shipId = 0,
    focusOnSegmentId = 0,
    pcsGroupId = null,
    pecExam = null,
    positionType = null,
    onClick,
    onChange,
    position = null,
    featureCollection,
    evaluation,
    pecId = null,
    pecApplicationId = null,
    pecLatitude = null,
    pecLongitude = null,
    pilotCertificateCoastalSegmentPerson = null,
    onInitFeatures,
    onFeatureMouseOver,
    onFeatureMouseOut,
    onFeatureClick,
}: GoogleMapProps) => {
    
    const mapStoreRef = useRef<StoreApi<IMapStoreState> | null>(null);
    if (!mapStoreRef.current) {
        mapStoreRef.current = createMapStore({
            showAis: showAis
        });
    }

    const [searchParams, _] = useSearchParams();
    
    const [selectedPosition, setSelectedPosition] = useState<google.maps.LatLng | null>(null);
    const [showMap, setShowMap] = useState(true);

    const darkmode = useDarkmode();
    const mapEvent = useMapEvent();
    const configuration = useConfiguration();

    const config = useStoreSelector(mapStoreRef.current!, (state) => state.config);
    const setConfig = useStoreSelector(mapStoreRef.current!, (state) => state.setConfig);

    const handleThemeChanged = () => {
        setShowMap(false);
    }

    const mapStore = mapStoreRef.current;
    const setFeatureMouseOver = useStoreSelector(mapStore, (state) => state.setFeatureMouseOver);
    const setFeatureMouseOut = useStoreSelector(mapStore, (state) => state.setFeatureMouseOut);
    const setMapTitle = useStoreSelector(mapStore, (state) => state.setMapTitle);
    const setMapClicked = useStoreSelector(mapStore, (state) => state.setMapClicked);
    const setShowLocations = useStoreSelector(mapStore, (state) => state.setShowLocations);
    const setIsAisActive = useStoreSelector(mapStore, (state) => state.setIsAisActive);
    const setPilotCertificateCoastalSegmentPerson = useStoreSelector(mapStore, (state) => state.setPilotCertificateCoastalSegmentPerson);

    const handleMapEvent = (mapEvent: MapEventProps | null) => {
        if (!mapEvent) return;
        switch (mapEvent.event) {
            case EventSystemNames.onFeatureMouseOver:
                setFeatureMouseOver(mapEvent.data.value);
                break;
            case EventSystemNames.onFeatureMouseOut:
                setFeatureMouseOut(0);
                break;
            case EventSystemNames.MapTitle:
                setMapTitle((!isNullOrEmpty(mapEvent.data.title)) ? mapEvent.data.title : "");
                break;
            default: return;
        }
    }

    const handleValueChanged = (val:any, setVal:(val:any) => void) => {
        setTimeout(() => {
            setVal(val);
        }, DEBOUNCE_TIMER);
    }

    useEffect(() => {
        if (!configuration) return;

        const lat = getNumericUrlParam("lat");
        const lng = getNumericUrlParam("lng");
        const config = deepCopyObject(configuration);

        config.defaultMapLocation = JSON.parse(config.defaultMapLocation);

        if (isNumeric(lat) &&
            isNumeric(lng) &&
            lat > 0 && lng > 0) {
            config.defaultMapLocation = { lat: lat, lng: lng };
        }

        setConfig(config);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuration]);

    useEffect(() => {
        const initialize = () => {
            const sLoc = getBooleanSearchParam(searchParams, "sloc");
            if (!sLoc) return;
            handleValueChanged(sLoc, setShowLocations);
        }
        initialize();
        return (() => {
            mapStoreRef.current = null;
        })
    }, []);

    useEffect(() => {
        handleThemeChanged();
    }, [darkmode]);

    useEffect(() => {
        if (showMap) return;
        setShowMap(true);
    }, [showMap]);

    useEffect(() => {
        handleMapEvent(mapEvent);
    }, [mapEvent]);

    useEffect(() => {
        handleValueChanged(pilotCertificateCoastalSegmentPerson, setPilotCertificateCoastalSegmentPerson);
    }, [pilotCertificateCoastalSegmentPerson, setPilotCertificateCoastalSegmentPerson]);

    useEffect(() => {
        handleValueChanged(showAis, setIsAisActive);
    }, [showAis, setIsAisActive]);

    useEffect(() => {
        handleValueChanged(showLocations, setShowLocations);
    }, [showLocations, setShowLocations]);

    if (!config || !showMap) {
        return null;
    }

    return (
        <MapStoreContext.Provider value={mapStoreRef.current!}>
            <GoogleMapContainer
                isInline={isInline}
                height={height}
                zoom={zoom}
                minZoom={minZoom}
                onClick={handleOnClick}>

                <MapContent
                    showSettings={showSettings}
                    showSearch={showSearch}
                    showFeatures={showFeatures}
                    showAis={showAis}
                    showPcs={showPcs}
                    showSeamap={showSeamap}
                    showCertificateLocations={showCertificateLocations}
                    showPilotageShipPosition={showPilotageShipPosition}
                    pilotageId={pilotageId}
                    incidentPilotId={incidentPilotId}
                    shipId={shipId}
                    focusOnSegmentId={focusOnSegmentId}
                    pcsGroupId={pcsGroupId}
                    pecExam={pecExam}
                    positionType={positionType}
                    position={position}
                    featureCollection={featureCollection}
                    evaluation={evaluation}
                    selectedPosition={selectedPosition}
                    autoUpdateSearchParams={autoUpdateSearchParams}
                    onClick={onClick}
                    onChange={onChange}
                    onInitFeatures={onInitFeatures}
                    onFeatureMouseOver={onFeatureMouseOver}
                    onFeatureMouseOut={onFeatureMouseOut}
                    onFeatureClick={onFeatureClick}
                    setSelectedPosition={setSelectedPosition}
                    pecId={pecId}
                    pecApplicationId={pecApplicationId}
                    pecLatitude={pecLatitude}
                    pecLongitude={pecLongitude}
                />

            </GoogleMapContainer>
        </MapStoreContext.Provider>
    )

    function handleOnClick(e: any) {
        e.stop();

        setMapClicked(e.detail);

        if (!onClick) return;

        setSelectedPosition(e.detail.latLng);
        onClick(e.detail.latLng);
    }
}

interface GoogleMapContainerProps {
    isInline?: boolean,
    height?: number | null,
    zoom?: number,
    minZoom?: number,
    onClick: (e: any) => void,
}

const GoogleMapContainer = ({
    children,
    isInline,
    height,
    zoom = 11,
    minZoom = 4,
    onClick,
}: PropsWithChildren<GoogleMapContainerProps>) => {

    const darkmode = useDarkmode();

    const mapId = useMapId();
    const config = useMapConfig();

    const getContainerClassName = () => {
        if (isInline) return "googlemap-container-inline";
        return "";
    }
    
    return (
        <div
            className={`googlemap-container ${getContainerClassName()}`} id={mapId}
            style={{
                height: height ? `${height}px` : "100%"
            }}>
            <APIProvider apiKey={config.googleMapApiKey} language="no" libraries={['geometry']}>
                <Map
                    onClick={onClick}
                    defaultCenter={config.defaultMapLocation}
                    defaultZoom={zoom}
                    minZoom={minZoom}
                    colorScheme={darkmode ? "DARK" : "LIGHT"}
                    mapId={config.googleMapId}>
                    {children}
                </Map>
            </APIProvider>
        </div>
    )
}
